import { create } from "zustand";
import type { Outcome, SpinResult } from "@karrtopelka/game-kit";

/**
 * Minimal Zustand store for the push-your-luck wheel (one store per app, hard rule).
 * Holds DISPLAY state only — server I/O lives in useRun; App.tsx bridges hook → store.
 *
 *   loading → idle → spinning --(Gauge replays the chain)--> result → idle → …
 *   (no daily lock; spin freely. bust just resets the run streak to 0.)
 */
export type WheelPhase = "loading" | "idle" | "spinning" | "result";

interface WheelState {
  phase: WheelPhase;
  streak: number;
  /** The resolved outcome chain the Gauge replays (last entry is terminal). */
  outcomes: Outcome[];
  lastResult: SpinResult | null;
  /** Streak before this spin, to detect a win/loss for the celebration/commiseration. */
  prevStreak: number;
  /** Highest streak reached this session (the run's peak — the thing to brag). */
  best: number;

  setStreak: (streak: number) => void;
  beginSpin: () => void;
  land: (res: SpinResult) => void;
  toIdle: () => void;
}

export const useWheelStore = create<WheelState>((set, get) => ({
  phase: "loading",
  streak: 0,
  outcomes: [],
  lastResult: null,
  prevStreak: 0,
  best: 0,

  setStreak: (streak) =>
    set({ phase: "idle", streak, best: Math.max(get().best, streak) }),

  // Clear the previous chain so the Gauge doesn't briefly animate stale outcomes before
  // the fresh ones arrive (it only animates a non-empty chain).
  beginSpin: () => set({ phase: "spinning", prevStreak: get().streak, outcomes: [] }),

  land: (res) =>
    set({
      phase: "result",
      streak: res.streak,
      outcomes: res.outcomes,
      lastResult: res,
      best: Math.max(get().best, res.streak),
    }),

  toIdle: () => set({ phase: "idle" }),
}));

/** Terminal outcome kind of a finished spin (the last chain entry). */
export function terminalKind(outcomes: Outcome[]): Outcome["kind"] | null {
  return outcomes.length > 0 ? outcomes[outcomes.length - 1]!.kind : null;
}
